/**
 * src/api/adminAI.js — Administration AI data layer.
 *
 * v1 derives most operational intelligence from GET /tasks/ which is
 * already available. GET /invoices/sync/status IS implemented on the
 * backend (see main.py get_sync_status()) and is fetched below to enrich
 * the Failed QB Sync view with real error messages/timestamps — the
 * remaining QB-specific endpoints listed below are not yet implemented
 * and are stubbed with try/catch fallbacks that return null.
 *
 * ──────────────────────────────────────────────────────────────────────
 * BACKEND ENDPOINTS NEEDED FOR LIVE BILLING INTELLIGENCE
 * ──────────────────────────────────────────────────────────────────────
 *
 * 1. GET /tasks/billing-ready
 *    Returns completed/delivered tasks that have not yet been invoiced.
 *    Response: Task[]  (same shape as /tasks/)
 *    Filter server-side: status IN ('Delivered','Completed')
 *      AND (invoiceId IS NULL OR qbSyncStatus != 'synced')
 *
 * 2. GET /tasks/missing-pod
 *    Returns delivered tasks without a linked proof record.
 *    Response: Task[]
 *
 * 3. GET /admin/billing-summary
 *    Aggregated billing snapshot:
 *    { uninvoicedCount, uninvoicedTotal, failedSyncCount,
 *      missingPodCount, overdueCount }
 *
 * Until those endpoints exist, AdminAI derives these signals from the
 * raw task array using field-level heuristics documented below.
 * ──────────────────────────────────────────────────────────────────────
 */

import { request } from "./client";

/** Fetch all tasks (dispatcher view). Reuses the existing /tasks/ endpoint. */
export function fetchAllTasksForAdmin() {
  return request("/tasks/", {
    authContext: { message: "Your session expired while loading admin task data." },
  });
}

/**
 * GET /invoices/sync/status — implemented on the backend (main.py
 * get_sync_status()). Returns the full QuickBooks sync attempt log:
 * { total_attempts, success_count, failed_count, items: [{ id, task_id,
 *   attempted_at, outcome, qb_invoice_id, doc_number, error_msg,
 *   triggered_by }] }
 *
 * Still wrapped in try/catch so a network hiccup or a future backend
 * rollback degrades gracefully — AdminAI falls back to task-field
 * heuristics when this returns null.
 */
export async function fetchQBSyncStatus() {
  try {
    const res = await request("/invoices/sync/status", {
      authContext: { message: "Your session expired while loading QuickBooks sync history." },
    });
    if (res.ok) return res.json();
    return null;
  } catch {
    return null;
  }
}

/**
 * Reduce the sync log's `items` array down to the single most recent
 * attempt per task. The backend already orders by attempted_at DESC, so
 * the first occurrence of a given task_id is the latest attempt.
 * Returns a Map<taskId, logEntry> for O(1) lookup while rendering rows.
 */
export function latestSyncAttemptByTask(syncLogItems) {
  const map = new Map();
  if (!Array.isArray(syncLogItems)) return map;
  for (const item of syncLogItems) {
    if (item?.task_id && !map.has(item.task_id)) {
      map.set(item.task_id, item);
    }
  }
  return map;
}

// ─── Task-level billing heuristics ─────────────────────────────────────────
// These derive billing state from fields that exist on the task record.
// Field names are checked defensively because the backend may use different
// naming conventions across versions.

/**
 * True if the task represents completed work with no linked QB invoice.
 * Checks common field name patterns for invoice linkage.
 */
export function isReadyToInvoice(task) {
  const status = (task.status || "").toLowerCase();
  if (status !== "delivered" && status !== "completed") return false;

  // Any of these fields indicate an invoice already exists
  const hasInvoice =
    task.invoiceId ||
    task.invoice_id ||
    task.quickbooksInvoiceId ||
    task.qb_invoice_id ||
    task.qbInvoiceId ||
    (task.qbSyncStatus && task.qbSyncStatus.toLowerCase() === "synced") ||
    (task.qb_sync_status && task.qb_sync_status.toLowerCase() === "synced");

  return !hasInvoice;
}

/**
 * True if a QB sync attempt for this task has failed.
 */
export function hasFailedQBSync(task) {
  const s =
    (task.qbSyncStatus || task.qb_sync_status || "").toLowerCase();
  return s === "failed" || s === "error";
}

/**
 * True if the task is delivered/completed but has no POD record.
 */
export function isMissingPOD(task) {
  const status = (task.status || "").toLowerCase();
  if (status !== "delivered" && status !== "completed") return false;
  return (
    !task.podPhotoReceived &&
    !task.podSignatureReceived &&
    !task.podReceivedAt &&
    !task.pod_received_at
  );
}

/**
 * True if the task is past its deadline and not in a terminal status.
 */
export function isOverdueActive(task) {
  const status = (task.status || "").toLowerCase();
  if (
    status === "delivered" ||
    status === "completed" ||
    status === "canceled" ||
    status === "failed"
  )
    return false;

  const deadline =
    task.deliveryDeadline ||
    task.delivery_deadline ||
    task.milestones?.deliveryDeadline ||
    task.caseDeadline ||
    task.surgeryTime;

  if (!deadline) return false;
  return new Date(deadline) < new Date();
}

/**
 * Returns the billable dollar amount for a task, or null if unknown.
 * Checks common field name patterns.
 */
export function getBillableAmount(task) {
  const raw =
    task.total ?? task.price ?? task.totalAmount ?? task.amount ?? task.billedAmount ?? null;
  if (raw == null) return null;
  const n = parseFloat(raw);
  return isNaN(n) ? null : n;
}

/**
 * Returns a human-readable QB sync label for display.
 */
export function qbSyncLabel(task) {
  const s = (task.qbSyncStatus || task.qb_sync_status || "").toLowerCase();
  if (!s) return "Not synced";
  if (s === "synced") return "Synced";
  if (s === "failed" || s === "error") return "Sync failed";
  if (s === "pending") return "Pending";
  return s;
}
